import React, { useEffect, useState } from 'react';
import { askOrbitAction } from '../../components/AskOrbit';
import { useSearchParams } from 'react-router-dom';
import { Lock, Sparkles } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import { useParticipant } from '../../hooks/useParticipant';
import type { ControlEvent, DepartmentDecision, DepartmentOverview, DepartmentPattern, DepartmentQuickWin, DepartmentWork, ReadinessResponse } from '../../types/aai';
import {
  Action, Banner, Chip, Divided, EmptyState, ErrorNote, ListRow, Loading, NotMeasured, PageBody, PageHead, Panel, PanelHead,
  RouteMark, Stat, StatusChip,
} from '../../components/aa';
import { relativeTime } from '../partner/format';
import { useRecordLinks } from '../../hooks/useRecordLinks';
import { brief, isReadyToUse, needsOwner } from '../owner/WorkRows';
import { nextFor, stateFor } from '../partner/work';
import { ActionUpdateDrawer } from '../champion/ActionUpdateDrawer';
import { ACTION_KIND_LABEL, isAwaiting, payloadText, shortName, sideOf, statusOf } from '../champion/actionStatus';
import { DecisionDrawer } from './DecisionDrawer';
import { PatternDrawer } from './PatternDrawer';
import { ReadinessDrawer } from '../employee/ReadinessDrawer';
import { departmentName, plural, useDepartment } from './useDepartment';
import { RoomHomePanel } from '../orbit-room/RoomHomePanel';
import { OrbitRoomPanel } from '../orbit-room/OrbitRoomPanel';
import { ClientWorkCard } from '../employee/EmployeeHome';
// import { HomeResults } from '../../components/HomeResults';
// import { ResultsBody } from './ResultsPage';

/*
 * The Department Head's Home: the numbers, the department as Orbit reads it,
 * then what needs the head. Every number is a department aggregate; nothing
 * here names a person's readiness. The Results block is commented out for now.
 */

/** What the program asked of this head and nobody has answered yet. */
const awaitingMe = (data: DepartmentOverview, me: string): ControlEvent[] => (data.actions || [])
  .filter((e) => isAwaiting(e) && sideOf(e, me) === 'recipient');

/*
const summaryLine = (data: DepartmentOverview, asks: number): string => {
  const parts = [plural(data.counts.decisions, 'decision', 'decisions'), plural(data.counts.projects, 'active project', 'active projects')];
  if (asks > 0) parts.push(plural(asks, 'request to answer', 'requests to answer'));
  if (data.counts.need_support !== null) parts.push(`${data.counts.need_support} people need support`);
  return parts.join(' · ');
};
*/

/*
 * The head onboards like everyone else. One strip, two states: until the
 * readiness baseline exists it asks for onboarding; afterwards it is the door
 * to My Readiness. No extra nav item either way.
 */
const STAGE_LABEL: Record<string, string> = {
  nascent: 'Starting out',
  early: 'Early',
  developing: 'Developing',
  mature: 'Mature',
  advanced: 'Advanced',
};

const OwnReadinessBanner: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const { context } = useParticipant();
  // The context flag is cached for the session, so it lags an onboarding that
  // just finished. The live readiness read decides; the flag only fills in
  // while that read is in flight or has failed.
  const mine = useAsync<ReadinessResponse>(() => aaiApi.getMyReadiness(), 'Could not load your readiness.');
  if (mine.loading) return null;
  let complete = Boolean(context?.readiness_complete);
  if (mine.data) complete = Boolean(mine.data.readiness);
  const mark = (
    <span className="grid h-[46px] w-[46px] shrink-0 place-items-center rounded-tile bg-canvas-inset text-ink">
      <Sparkles size={22} strokeWidth={2} />
    </span>
  );
  if (!complete) {
    // Parked for now: the readiness onboarding card.
    return null;
    /*
    return (
      <Banner
        className="mb-5"
        size="lg"
        mark={mark}
        kicker="Onboarding"
        title="Set your own readiness baseline"
        desc="A short Orbit conversation about how you work today. Your answers stay yours; the team only ever sees aggregates."
        action={<Action arrow to="/onboarding">Start onboarding</Action>}
      />
    );
    */
  }
  const stage = mine.data?.readiness?.stage;
  let desc = 'Your five readiness dimensions, what improved, and the next step that fits your work.';
  if (stage) desc = `${STAGE_LABEL[stage] || stage} stage · your five readiness dimensions, what improved, and the next step that fits your work.`;
  return (
    <Banner
      className="mb-5"
      size="lg"
      mark={mark}
      kicker="My readiness"
      title="Your own AI readiness"
      desc={desc}
      action={<Action variant="quiet" arrow onClick={onOpen}>Open My Readiness</Action>}
    />
  );
};

const CompactWorkRow: React.FC<{ request: DepartmentWork; onOpen: (id: string) => void }> = ({ request, onOpen }) => {
  let kind: 'quick_win' | 'project' = 'project';
  let status = stateFor(request);
  let label = 'Delivery Project';
  if (isReadyToUse(request)) {
    kind = 'quick_win';
    status = 'ready_short';
    label = 'Quick Win';
  }
  return (
    <ListRow
      mark={<RouteMark kind={kind} size="lg" />}
      title={request.title}
      badge={needsOwner(request) && <Chip tone="warn" dot>Needs you</Chip>}
      foot={`${label} · ${nextFor(request)}`}
      right={<StatusChip status={status} />}
      onClick={() => onOpen(request.id)}
      arrow
    />
  );
};

export const QuickWinRow: React.FC<{ win: DepartmentQuickWin; compact?: boolean }> = ({ win, compact = false }) => {
  let usage = 'Usage protected under the reporting threshold';
  if (win.usage_pct !== null) usage = `${win.usage_pct}% of the team using it`;
  let scope = 'Company';
  if (win.scope === 'team') scope = 'Team';
  let setup = 'Published';
  if (win.use_case_id) setup = 'Already approved';
  if (compact) {
    return (
      <ListRow
        mark={<RouteMark kind="quick_win" size="lg" />}
        title={win.name}
        foot={`Quick Win · ${usage}`}
        right={<StatusChip status="ready_short" />}
      />
    );
  }
  return (
    <ListRow
      mark={<RouteMark kind="quick_win" size="lg" />}
      title={win.name}
      badge={<StatusChip status="ready_short" />}
      desc={brief(win.problem)}
      meta={[
        { label: 'Scope', value: scope },
        { label: 'For', value: win.department || 'Whole company' },
        { label: 'Setup', value: setup },
      ]}
      foot={usage}
    />
  );
};

const DepartmentHome: React.FC = () => {
  const clientId = localStorage.getItem('client_id') || '';
  const overview = useDepartment();
  const setOpenProject = useRecordLinks().project;
  const [openDecision, setOpenDecision] = useState<DepartmentDecision | null>(null);
  const [openPattern, setOpenPattern] = useState<DepartmentPattern | null>(null);
  const [openAction, setOpenAction] = useState<ControlEvent | null>(null);
  const [readinessOpen, setReadinessOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const me = localStorage.getItem('user_email') || '';

  // The old My Readiness address lands here with the drawer already open.
  useEffect(() => {
    if (params.get('open') !== 'readiness') return;
    setReadinessOpen(true);
    setParams({}, { replace: true });
  }, [params, setParams]);

  if (overview.loading) return <PageBody><Loading label="Loading your department" /></PageBody>;
  if (overview.error || !overview.data) return <PageBody><ErrorNote message={overview.error || 'Something went wrong.'} /></PageBody>;

  const data = overview.data;
  const name = departmentName(data);
  const asks = awaitingMe(data, me);
  const next = data.decisions[0] || null;
  const isLead = data.department.is_lead;

  let decisionFoot = 'Recorded lead decides';
  if (isLead) decisionFoot = 'Yours as department head';
  const decisionRow = (d: DepartmentDecision) => {
    let chip = 'Your review';
    if (d.status === 'needs_info') chip = 'Detail requested';
    let valueFoot: string | undefined;
    if (d.est_value_band) valueFoot = 'Forecast, not confirmed';
    return (
      <ListRow
        key={d.id}
        mark={<RouteMark kind="project" size="lg" />}
        title={d.title}
        badge={<Chip tone="warn" dot>{chip}</Chip>}
        desc={brief(d.detail)}
        meta={[
          { label: 'Potential value', value: d.est_value_band || 'Not estimated', foot: valueFoot },
          { label: 'Decision', value: 'Clear, ask, or decline', foot: decisionFoot },
          { label: 'Since', value: relativeTime(d.since) },
        ]}
        right={<Action size="sm" variant="quiet" arrow onClick={() => setOpenDecision(d)}>Review</Action>}
      />
    );
  };
  let leadNote = 'No lead is recorded for this department yet, so nothing can be cleared. Ask an admin to set one.';
  if (data.department.lead_email) leadNote = `${data.department.lead_email} is the recorded lead. You can read the queue; an admin can make you the lead under Settings.`;
  let decisionsTitle = 'Nothing is waiting on you';
  if (data.decisions.length > 0) decisionsTitle = `${plural(data.decisions.length, 'request', 'requests')} waiting on you`;
  let supportValue: React.ReactNode = data.counts.need_support;
  if (data.counts.need_support === null) supportValue = 'n/a';

  return (
    <PageBody>
      <PageHead
        align="center"
        eyebrow="Department Head"
        title={name}
        actions={(
          <>
            <Action
              arrow
              disabled={!next}
              reason="Nothing is waiting on your decision"
              onClick={() => { if (next) setOpenDecision(next); }}
            >
              Review next decision
            </Action>
          </>
        )}
      />

      {localStorage.getItem('is_expert') === '1' && <ClientWorkCard />}

      <OwnReadinessBanner onOpen={() => setReadinessOpen(true)} />

      <RoomHomePanel variant="member" />

      <div className="mb-5 grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Stat label="Decisions waiting" value={data.counts.decisions} tone={data.counts.decisions > 0 ? 'attention' : 'default'} />
        <Stat label="Active work" value={data.counts.work} />
        <Stat label="Quick Wins" value={data.counts.quick_wins} />
        <Stat label="People needing support" value={supportValue} />
      </div>

      {asks.length > 0 && (
        <Panel className="mb-5">
          <PanelHead
            kicker="Needs your response"
            title={`${plural(asks.length, 'request', 'requests')} for ${name}`}
          />
          <Divided>
            {asks.map((e) => {
              const state = statusOf(e.status);
              return (
                <ListRow
                  key={e.id}
                  mark={<RouteMark kind="adoption" size="lg" />}
                  title={e.subject}
                  badge={<Chip tone={state.tone} dot>{state.label}</Chip>}
                  desc={payloadText(e, 'note') || undefined}
                  meta={[
                    { label: 'Kind', value: ACTION_KIND_LABEL[e.kind] || e.kind },
                    { label: 'Asked by', value: shortName(e.actor_email), foot: e.actor_email || undefined },
                    { label: 'When', value: relativeTime(e.created_at) },
                  ]}
                  right={<Action size="sm" arrow onClick={() => setOpenAction(e)}>Respond</Action>}
                />
              );
            })}
          </Divided>
        </Panel>
      )}

      {clientId && (
        <div className="mb-5">
          <OrbitRoomPanel viewer="department" clientId={clientId} />
        </div>
      )}

      {!isLead && data.decisions.length > 0 && (
        <Banner
          className="mb-5"
          tone="quiet"
          size="sm"
          mark={<span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-tile bg-ink text-white"><Lock size={16} strokeWidth={2} /></span>}
          kicker="Role is not permission"
          title={`Requests here are cleared by the recorded ${name} lead`}
          desc={leadNote}
        />
      )}

      <Panel className="mb-5">
        <PanelHead
          kicker="Needs your review"
          title={decisionsTitle}
          // Open requests door hidden for now; decisions are taken from the rows below.
          // action={<Action size="sm" variant="quiet" arrow to="/requests">Open requests</Action>}
        />
        {data.decisions.length === 0 && (
          <EmptyState title="All clear" reason="Requests from your department that need you appear here." />
        )}
        {data.decisions.length > 0 && <Divided>{data.decisions.map(decisionRow)}</Divided>}
      </Panel>

      <div className="mb-5 grid gap-5 lg:grid-cols-2">
        <Panel floor>
          <PanelHead kicker="Orbit findings" title="Shared patterns" />
          {data.patterns.length === 0 && (
            <EmptyState title="No shared pattern yet" reason="A pattern appears once two or more requests share a capability and one of them is from your department." />
          )}
          <Divided>
            {data.patterns.map((p) => (
              <ListRow
                key={p.key}
                mark={<span className="grid h-[37px] w-[37px] shrink-0 place-items-center rounded-tile bg-[rgba(255,234,0,0.25)] font-sans text-[13px] font-bold tabular-nums text-[#8A6D00]">{p.count}</span>}
                title={p.label}
                foot={p.departments.join(' · ')}
                onClick={() => setOpenPattern(p)}
                arrow
              />
            ))}
          </Divided>
        </Panel>

        <Panel floor>
          <PanelHead
            kicker="In progress"
            title="Department work"
            action={<Action size="sm" variant="quiet" arrow to="/projects">See all</Action>}
          />
          {data.work.length === 0 && data.quick_wins.length === 0 && (
            <EmptyState title="No work in flight" reason="Approved requests and published Quick Wins for your department appear here." action={askOrbitAction()} />
          )}
          <Divided>
            {data.work.slice(0, 3).map((r) => <CompactWorkRow key={r.id} request={r} onOpen={setOpenProject} />)}
            {data.quick_wins.slice(0, 3 - Math.min(3, data.work.length)).map((w) => <QuickWinRow key={w.id} win={w} compact />)}
          </Divided>
        </Panel>
      </div>

      {/* AI Adoption is hidden for now, banner included. */}

      {data.readiness === null && (
        <div className="mt-3 text-center"><NotMeasured reason="No department is recorded for you yet, so there is no team to report on" /></div>
      )}

      {openDecision && (
        <DecisionDrawer
          decision={openDecision}
          department={name}
          isLead={isLead}
          readiness={data.readiness}
          onClose={() => setOpenDecision(null)}
          onDecided={overview.reload}
        />
      )}
      {openPattern && (
        <PatternDrawer pattern={openPattern} department={name} onOpenProject={setOpenProject} onClose={() => setOpenPattern(null)} />
      )}
      {openAction && <ActionUpdateDrawer event={openAction} onUpdated={() => { setOpenAction(null); overview.reload(); }} onClose={() => setOpenAction(null)} />}
      {readinessOpen && <ReadinessDrawer onClose={() => setReadinessOpen(false)} />}
      {/*
      <HomeResults
        ready={!overview.loading}
        title="What your team got back"
        sub="Live results use reviewed evidence. Expected value remains a forecast until confirmed."
      >
        <ResultsBody />
      </HomeResults>
      */}
    </PageBody>
  );
};

export default DepartmentHome;
